import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TomorrowBookings } from "@/components/TomorrowBookings";
import { useWaMsg, waUrl } from "@/lib/waTemplates";
import { CalendarDays, ChevronLeft, ChevronRight, Pencil, X, Loader2, FilePlus2, ExternalLink, MessageCircle } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const br = (s) => (s ? s.split("-").reverse().join("/") : "");
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const inCls = "bg-zinc-900 border-zinc-800";

function CustomerPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [mode, setMode] = useState("search");
  const [nc, setNc] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => { api.get("/booking/customer-search", { params: { q } }).then(({ data }) => setRes(data)).catch(() => setRes([])); }, 300);
    return () => clearTimeout(t);
  }, [q]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/customers", { name: nc.name.trim(), phone: nc.phone, email: nc.email.trim() });
      onChange({ id: data.id, name: data.name, phone: data.phone }); setMode("search");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (value) {
    return <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" data-testid="appt-customer-selected"><span className="text-zinc-100 font-bold">{value.name}</span><button type="button" data-testid="appt-customer-change" onClick={() => onChange(null)} className="text-xs text-amber-300">Trocar</button></div>;
  }
  if (mode === "new") {
    return (
      <div className="space-y-2 rounded-md border border-zinc-800 p-3" data-testid="appt-new-customer">
        <Input data-testid="appt-nc-name" placeholder="Nome" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} className={inCls} />
        <Input data-testid="appt-nc-phone" placeholder="Telefone com DDD" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} className={inCls} />
        <Input data-testid="appt-nc-email" placeholder="E-mail" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} className={inCls} />
        <div className="flex gap-2"><Button type="button" variant="outline" onClick={() => setMode("search")} className={inCls}>Voltar</Button>
          <Button type="button" data-testid="appt-nc-save" disabled={busy || !nc.name.trim() || !nc.email.trim()} onClick={create} className="bg-green-brand text-zinc-950 font-bold">Cadastrar cliente</Button></div>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Input data-testid="appt-customer-search" placeholder="Buscar por nome ou placa" value={q} onChange={(e) => setQ(e.target.value)} className={inCls} />
      {res.length > 0 && <ul className="rounded-md border border-zinc-800 divide-y divide-zinc-800 max-h-40 overflow-auto">{res.map((c) => (
        <li key={c.id}><button type="button" data-testid={`appt-customer-opt-${c.id}`} onClick={() => onChange(c)} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800">{c.name} <span className="text-xs text-zinc-500">{c.phone}</span></button></li>
      ))}</ul>}
      <button type="button" data-testid="appt-customer-new" onClick={() => setMode("new")} className="text-xs text-amber-300">+ Cadastrar novo cliente</button>
    </div>
  );
}

export function ApptWa({ a, big }) {
  const waMsg = useWaMsg();
  const href = waUrl(a.phone, waMsg("booking_confirm", { cliente: a.name, placa: a.plate, data: `${br(a.date)} às ${a.time} (${a.service_name})` }));
  if (!href) { return big ? <p className="text-xs text-zinc-500">Cliente sem telefone cadastrado para WhatsApp.</p> : null; }
  return <a data-testid={`appt-wa-${a.id}`} href={href} target="_blank" rel="noopener noreferrer" className={`${big ? "h-10 px-4 text-sm" : "h-8 px-2 text-xs"} rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-bold inline-flex items-center gap-1`}><MessageCircle className="h-3.5 w-3.5" /> {big ? "Confirmar pelo WhatsApp" : "WhatsApp"}</a>;
}

export function ApptOsAction({ a }) {
  const nav = useNavigate();
  if (a.order_id) {
    return <Link data-testid={`appt-os-link-${a.id}`} to={`/owner/ordens/${a.order_id}`} className="h-8 px-2 rounded-md border border-green-brand/60 text-xs text-green-300 inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> OS aberta</Link>;
  }
  return <button type="button" data-testid={`appt-open-os-${a.id}`} onClick={() => nav(`/owner/nova-ordem?agendamento=${a.id}`)} className="h-8 px-2 rounded-md bg-green-brand text-zinc-950 text-xs font-bold inline-flex items-center gap-1"><FilePlus2 className="h-3 w-3" /> Abrir OS</button>;
}

export function ApptDialog({ open, appt, prefill, onClose, onSaved }) {
  const [cust, setCust] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [f, setF] = useState({ plate: "", vehicle_label: "", date: "", time: "", service_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  useEffect(() => {
    if (!open) { return; }
    setSaved(null);
    setCust(appt ? { id: appt.customer_id, name: appt.name } : prefill?.customer || null);
    setF(appt ? { plate: appt.plate || "", vehicle_label: appt.vehicle_label || "", date: appt.date, time: appt.time, service_name: appt.service_name || "", note: appt.note || "" }
      : { plate: prefill?.plate || "", vehicle_label: prefill?.vehicle_label || "", date: prefill?.date || "", time: prefill?.time || "", service_name: prefill?.service_name || "", note: "" });
  }, [open, appt, prefill]);
  useEffect(() => {
    if (!cust?.id) { setVehicles([]); return; }
    api.get(`/customers/${cust.id}/vehicles`).then(({ data }) => setVehicles(data || [])).catch(() => setVehicles([]));
  }, [cust?.id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, customer_id: cust.id };
      if (appt) { await api.put(`/booking/appointments/${appt.id}`, body); toast.success("Agendamento atualizado"); onSaved(); return; }
      const { data } = await api.post("/booking/appointments", body);
      toast.success("Agendamento criado"); setSaved(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { saved ? onSaved() : onClose(); } }}>
      <DialogContent data-testid="appt-dialog" className="bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto">
        {saved ? (
          <div data-testid="appt-saved" className="space-y-3">
            <DialogHeader><DialogTitle>Agendamento salvo</DialogTitle><DialogDescription>{saved.name} · {saved.plate} · {br(saved.date)} às {saved.time} · {saved.service_name}</DialogDescription></DialogHeader>
            <p className="text-sm text-zinc-400">Envie a confirmação ao cliente (mensagem editável em Configurações → Atendimento).</p>
            <div className="flex justify-end gap-2"><ApptWa a={saved} big /><Button data-testid="appt-saved-close" variant="outline" onClick={onSaved} className={inCls}>Fechar</Button></div>
          </div>
        ) : <>
        <DialogHeader><DialogTitle>{appt ? "Editar agendamento" : "Novo agendamento"}</DialogTitle><DialogDescription>Cliente, veículo, data, horário e serviço.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><div className="text-[11px] font-mono-caps uppercase text-zinc-500 mb-1">Cliente</div><CustomerPicker value={cust} onChange={setCust} /></div>
          {cust && (
            <div>
              <div className="text-[11px] font-mono-caps uppercase text-zinc-500 mb-1">Veículo</div>
              {vehicles.length > 0 && <div className="flex flex-wrap gap-1 mb-2">{vehicles.map((v) => (
                <button key={v.plate} type="button" data-testid={`appt-vehicle-${v.plate}`} onClick={() => setF({ ...f, plate: v.plate, vehicle_label: [v.make, v.model].filter(Boolean).join(" ") })}
                  className={`h-8 px-2 rounded-md border text-xs ${f.plate === v.plate ? "border-amber-brand text-amber-200" : "border-zinc-700 text-zinc-300"}`}>{v.plate} {v.model}</button>
              ))}</div>}
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="appt-plate" placeholder="Placa" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} className={inCls} />
                <Input data-testid="appt-vehicle-label" placeholder="Modelo (opcional)" value={f.vehicle_label} onChange={set("vehicle_label")} className={inCls} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="appt-date" type="date" value={f.date} onChange={set("date")} className={inCls} />
            <Input data-testid="appt-time" type="time" value={f.time} onChange={set("time")} className={inCls} />
          </div>
          <Input data-testid="appt-service" placeholder="Serviço" value={f.service_name} onChange={set("service_name")} className={inCls} />
          <Textarea data-testid="appt-note" placeholder="Observação" rows={2} value={f.note} onChange={set("note")} className={inCls} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className={inCls}>Cancelar</Button>
            <Button data-testid="appt-save" disabled={busy || !cust || !f.plate || !f.date || !f.time || !f.service_name.trim()} onClick={save} className="bg-amber-brand hover:bg-amber-brandDark text-black font-bold">Salvar</Button>
          </div>
        </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

function ApptRow({ a, onEdit, onCancel }) {
  return (
    <li data-testid={`appt-row-${a.id}`} className="py-2 flex items-center justify-between gap-2 flex-wrap text-sm">
      <span><b className="text-zinc-100">{br(a.date)} {a.time}</b> <span className="text-zinc-300">{a.name}</span> <span className="font-mono text-zinc-400">{a.plate}</span> <span className="text-zinc-500">· {a.service_name}{a.note ? ` · ${a.note}` : ""}</span></span>
      <span className="flex gap-1">
        <ApptOsAction a={a} />
        <ApptWa a={a} />
        {!a.order_id && <>
        <button type="button" data-testid={`appt-edit-${a.id}`} onClick={() => onEdit(a)} className="h-8 px-2 rounded-md border border-zinc-700 text-xs text-zinc-200 inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Editar</button>
        <button type="button" data-testid={`appt-cancel-${a.id}`} onClick={() => onCancel(a)} className="h-8 px-2 rounded-md border border-red-800 text-xs text-red-300 inline-flex items-center gap-1"><X className="h-3 w-3" /> Cancelar</button>
        </>}
      </span>
    </li>
  );
}

function MonthCalendar({ reloadKey, onEdit, onCancel }) {
  const now = new Date();
  const [ym, setYm] = useState([now.getFullYear(), now.getMonth()]);
  const [list, setList] = useState(null);
  const [day, setDay] = useState(null);
  const key = `${ym[0]}-${pad(ym[1] + 1)}`;
  useEffect(() => { setList(null); api.get("/booking/appointments", { params: { month: key } }).then(({ data }) => setList(data)).catch(() => setList([])); }, [key, reloadKey]);
  const first = new Date(ym[0], ym[1], 1);
  const days = new Date(ym[0], ym[1] + 1, 0).getDate();
  const cells = [...Array(first.getDay()).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const byDay = (list || []).reduce((m, a) => { (m[a.date] = m[a.date] || []).push(a); return m; }, {});
  const today = isoDay(now);
  const move = (n) => { const d = new Date(ym[0], ym[1] + n, 1); setYm([d.getFullYear(), d.getMonth()]); setDay(null); };
  const sel = day ? byDay[day] || [] : [];
  return (
    <div data-testid="appt-calendar" className="rounded-xl border border-zinc-800 p-3">
      <div className="flex items-center justify-between mb-2">
        <button type="button" data-testid="appt-cal-prev" onClick={() => move(-1)} className="h-8 w-8 grid place-items-center rounded-md hover:bg-zinc-800"><ChevronLeft className="h-4 w-4" /></button>
        <div data-testid="appt-cal-title" className="font-bold text-zinc-100">{MONTHS[ym[1]]} de {ym[0]}</div>
        <button type="button" data-testid="appt-cal-next" onClick={() => move(1)} className="h-8 w-8 grid place-items-center rounded-md hover:bg-zinc-800"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-zinc-500 mb-1">{["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => <div key={d}>{d}</div>)}</div>
      {!list ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-green-brand" /></div> : (
        <div className="grid grid-cols-7 gap-1">
          {cells.map((n, i) => {
            if (!n) { return <div key={`e${i}`} />; }
            const iso = `${key}-${pad(n)}`;
            const cnt = (byDay[iso] || []).length;
            return (
              <button key={iso} type="button" data-testid={`appt-cal-day-${iso}`} onClick={() => setDay(iso)}
                className={`h-11 rounded-md text-sm relative border ${day === iso ? "border-amber-brand" : "border-transparent"} ${iso === today ? "bg-green-brand text-zinc-950 font-black" : "bg-zinc-900 text-zinc-200 hover:bg-zinc-800"}`}>
                {n}
                {cnt > 0 && <span data-testid={`appt-cal-mark-${iso}`} className={`absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full px-1 text-[9px] font-bold leading-tight ${iso === today ? "bg-zinc-950 text-green-300" : "bg-amber-brand text-black"}`}>{cnt}</span>}
              </button>
            );
          })}
        </div>
      )}
      {day && (
        <div data-testid="appt-cal-day-list" className="mt-3">
          <div className="text-xs font-bold text-zinc-300 mb-1">Agendados em {br(day)}</div>
          {sel.length === 0 ? <div className="text-sm text-zinc-500">Nenhum carro agendado neste dia.</div> : <ul className="divide-y divide-zinc-800">{sel.map((a) => <ApptRow key={a.id} a={a} onEdit={onEdit} onCancel={onCancel} />)}</ul>}
        </div>
      )}
    </div>
  );
}

export function BookingScheduler({ externalKey = 0, initialPrefill = null, autoOpenNew = false }) {
  const [reloadKey, setReloadKey] = useState(0);
  const [dlg, setDlg] = useState({ open: !!(initialPrefill || autoOpenNew), appt: null, prefill: initialPrefill || null });
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => { api.get("/booking/appointments").then(({ data }) => setUpcoming(data || [])).catch(() => setUpcoming([])); }, [reloadKey, externalKey]);
  useEffect(() => { if (initialPrefill || autoOpenNew) { setDlg({ open: true, appt: null, prefill: initialPrefill || null }); } }, [initialPrefill, autoOpenNew]);
  const reload = () => setReloadKey((k) => k + 1);
  const onEdit = (a) => setDlg({ open: true, appt: a, prefill: null });
  const onCancel = async (a) => {
    if (!window.confirm(`Cancelar o agendamento de ${a.name} em ${br(a.date)} às ${a.time}?`)) { return; }
    try { await api.patch(`/booking/appointments/${a.id}`, { action: "cancel" }); toast.success("Agendamento cancelado"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <TomorrowBookings onNew={() => setDlg({ open: true, appt: null, prefill: null })} reloadKey={reloadKey + externalKey * 1000} Action={ApptOsAction} />
      <section data-testid="appt-upcoming" className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3">
        <div className="flex items-center gap-2 font-bold text-zinc-100"><CalendarDays className="h-4 w-4 text-amber-brand" /> Próximos agendamentos <span className="text-xs text-zinc-500 font-normal">({upcoming.length})</span></div>
        {upcoming.length === 0 ? <div className="text-sm text-zinc-500" data-testid="appt-upcoming-empty">Nenhum agendamento nos próximos dias.</div>
          : <ul className="divide-y divide-zinc-800">{upcoming.map((a) => <ApptRow key={a.id} a={a} onEdit={onEdit} onCancel={onCancel} />)}</ul>}
        <MonthCalendar reloadKey={reloadKey + externalKey * 1000} onEdit={onEdit} onCancel={onCancel} />
      </section>
      <ApptDialog open={dlg.open} appt={dlg.appt} prefill={dlg.prefill} onClose={() => setDlg({ open: false, appt: null, prefill: null })} onSaved={() => { setDlg({ open: false, appt: null, prefill: null }); reload(); }} />
    </div>
  );
}
